/**
 * Limites de mídia da publicação de projetos.
 *
 * Precisa espelhar as constantes de
 * backend/src/modules/posts/services/GenerateUploadPresignedUrlService.ts —
 * o backend é quem manda, aqui só evitamos que o aluno descubra o limite
 * depois de esperar um upload de 100 MB.
 */

export const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;

export const MAX_VIDEO_SIZE_BYTES = 100 * 1024 * 1024;

export const MAX_VIDEO_DURATION_SECONDS = 180;

/** Únicos formatos que o Rekognition analisa em vídeo (H.264). */
export const ALLOWED_VIDEO_MIME_TYPES = ['video/mp4', 'video/quicktime'];

/**
 * Extensão → MIME. Fallback para quando `file.type` vem vazio: iPhone entrega
 * `.MOV` sem tipo em boa parte dos navegadores, e drag-and-drop de arquivos
 * vindos de nuvem também.
 */
const EXTENSION_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  heic: 'image/heic',
  heif: 'image/heif',
  bmp: 'image/bmp',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  avi: 'video/x-msvideo',
  webm: 'video/webm',
};

/**
 * O tipo do arquivo como o resto do pipeline deve enxergá-lo. Todo lugar que
 * decide algo a partir do formato precisa passar por aqui — usar `file.type`
 * cru recusa arquivos legítimos que o navegador simplesmente não soube
 * classificar.
 */
export function resolveMimeType(file: File): string {
  if (file.type) return file.type;

  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return EXTENSION_MIME[ext] ?? '';
}

export function formatMegabytes(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))}MB`;
}

export function formatMinutes(seconds: number): string {
  const minutes = seconds / 60;
  return minutes === 1 ? '1 minuto' : `${minutes} minutos`;
}

/** Metadados de arquivo local carregam rápido; passou disso, algo travou. */
const PROBE_TIMEOUT_MS = 10000;

export interface IVideoProbe {
  /** Duração em segundos, ou null quando o navegador não soube informar. */
  durationSeconds: number | null;
  /**
   * Falso quando este navegador não consegue decodificar a trilha de vídeo.
   *
   * O caso que motivou isto: o iPhone grava em HEVC (H.265) por padrão, dentro
   * de um contêiner .MOV. O contêiner passa em qualquer checagem de MIME, mas
   * Chrome e Firefox não têm o decodificador — o vídeo publicava e aparecia
   * como um quadrado preto para quem tentasse assistir.
   */
  isDecodable: boolean;
}

/**
 * Abre o vídeo só até os metadados para descobrir duração e se o navegador
 * consegue decodificá-lo.
 *
 * É uma medida do navegador de quem está publicando, não do arquivo em si: um
 * vídeo recusado aqui pode tocar em outro navegador. Ainda assim é o melhor
 * sinal disponível no cliente, e serve justamente porque quem publica e quem
 * assiste costumam usar navegadores parecidos — se não abre aqui, provavelmente
 * não abre para a turma.
 */
export function probeVideo(file: File): Promise<IVideoProbe> {
  return new Promise(resolve => {
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement('video');

    let timeout = 0;

    const finish = (probe: IVideoProbe): void => {
      window.clearTimeout(timeout);
      video.onloadedmetadata = null;
      video.onerror = null;
      URL.revokeObjectURL(objectUrl);
      resolve(probe);
    };

    // Sem isto o anexo some: existe arquivo que não dispara nem
    // loadedmetadata nem error, e a promise ficaria pendente para sempre,
    // segurando junto todos os outros arquivos do mesmo envio.
    timeout = window.setTimeout(() => {
      // Estourou o tempo: não sabemos se toca. Deixamos passar em vez de
      // recusar por falta de informação — quem decide o limite é o servidor.
      finish({ durationSeconds: null, isDecodable: true });
    }, PROBE_TIMEOUT_MS);

    video.onloadedmetadata = () => {
      const { duration, videoWidth } = video;

      finish({
        durationSeconds:
          Number.isFinite(duration) && duration > 0 ? duration : null,
        // Metadados lidos mas largura zero: o contêiner abriu e a trilha de
        // vídeo não. É como o Chrome reage a parte dos arquivos HEVC.
        isDecodable: videoWidth > 0,
      });
    };

    // Nem o contêiner abriu — o caminho mais comum para HEVC no Chrome.
    video.onerror = () => finish({ durationSeconds: null, isDecodable: false });

    video.preload = 'metadata';
    video.src = objectUrl;
  });
}

/**
 * Duração do vídeo, ou null quando o navegador não soube informar. Nesse caso
 * deixamos passar: quem manda no limite de duração é o servidor, e barrar aqui
 * por falta de informação puniria o aluno por um limite do navegador dele.
 */
export async function getVideoDuration(file: File): Promise<number | null> {
  const { durationSeconds } = await probeVideo(file);
  return durationSeconds;
}
